import React from "react";
import { Tooltip } from "antd";
import { useDashTheme } from "../../hooks/useDashTheme";

/**
 * micro — the Classic lede's visualization vocabulary.
 *
 * One chart idiom per metric, chosen FOR the metric (approved in mock v5):
 *   presence     → Dots        (countable people, absent ones hollow-red)
 *   %-vs-target  → Bullet      (value bar against a target tick + good band)
 *   composition  → KSplit      (active/idle as one divided bar)
 *   progress n/m → GoalCells   (countable cells, lit = done)
 *   trend        → Spark       (only where trend genuinely is the story)
 *   state        → DeltaChip   (▲/▼ as a coloured pill, read before the value)
 *
 * All motion is CSS keyframes injected once by <ClassicMotion/>, and every
 * animation stands down under prefers-reduced-motion (the media query kills
 * them wholesale — same policy as the mock).
 */

export const CLASSIC_KEYFRAMES = `
@keyframes tgClRise{from{opacity:0;transform:translateY(10px)}to{opacity:1;transform:none}}
@keyframes tgClGrow{from{transform:scaleY(0)}to{transform:scaleY(1)}}
@keyframes tgClWiden{from{transform:scaleX(0)}to{transform:scaleX(1)}}
.tgcl-reveal{opacity:0;transform:translateY(12px);transition:opacity .5s cubic-bezier(.16,1,.3,1),transform .5s cubic-bezier(.16,1,.3,1)}
.tgcl-reveal.tgcl-in{opacity:1;transform:none}
.tgcl-grow{transform-origin:bottom;animation:tgClGrow .55s cubic-bezier(.16,1,.3,1) both}
.tgcl-widen{transform-origin:left;animation:tgClWiden .7s cubic-bezier(.16,1,.3,1) both}
.tgcl-shim{background:linear-gradient(90deg,var(--shim-a) 25%,var(--shim-b) 37%,var(--shim-a) 63%);background-size:400% 100%;animation:tgClShim 1.15s infinite}
@keyframes tgClShim{0%{background-position:100% 0}100%{background-position:0 0}}
@media (prefers-reduced-motion:reduce){
  .tgcl-reveal{opacity:1;transform:none;transition:none}
  .tgcl-grow,.tgcl-widen{animation:none}
  .tgcl-shim{animation:none;background:var(--shim-a)}
}
`;

export function ClassicMotion() {
  return <style>{CLASSIC_KEYFRAMES}</style>;
}

/** Loading shimmer. Uses surfaceMuted→borderStrong so the sweep is visible in
 *  BOTH themes (surfaceMuted→border was near-invisible in light mode). */
export function Shim({ w = "100%", h = 12, r = 8, style }) {
  const t = useDashTheme();
  return (
    <div className="tgcl-shim" aria-hidden style={{
      width: w, height: h, borderRadius: r,
      "--shim-a": t.c.surfaceMuted, "--shim-b": t.c.borderStrong, ...style,
    }} />
  );
}

/**
 * Scroll reveal with the lesson from the mock baked in: choreography must
 * never be able to leave content invisible. IO reveals as cards enter; a
 * 1.4s safety net reveals anything IO missed (broken embeds, webviews).
 */
export function useReveal() {
  const ref = React.useRef(null);
  React.useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    el.classList.add("tgcl-reveal");
    let io = null;
    if (typeof IntersectionObserver === "function") {
      io = new IntersectionObserver((es) => {
        es.forEach((e) => { if (e.isIntersecting) { e.target.classList.add("tgcl-in"); io.unobserve(e.target); } });
      }, { threshold: 0.1 });
      io.observe(el);
    } else {
      el.classList.add("tgcl-in");
    }
    const t = setTimeout(() => el.classList.add("tgcl-in"), 1400);
    return () => { clearTimeout(t); if (io) io.disconnect(); };
  }, []);
  return ref;
}

/* ── the idioms ──────────────────────────────────────────────────────────── */

export function DeltaChip({ dir = "flat", children }) {
  const t = useDashTheme();
  const map = {
    up: { bg: t.status.positiveSoft, fg: t.status.positive },
    down: { bg: t.status.negativeSoft, fg: t.status.negative },
    flat: { bg: t.c.surfaceMuted, fg: t.c.textTertiary },
  };
  const c = map[dir] || map.flat;
  return (
    <span style={{
      display: "inline-flex", alignItems: "center", height: 18, padding: "0 8px",
      borderRadius: 999, fontSize: 10.5, fontWeight: 700, letterSpacing: 0.1,
      background: c.bg, color: c.fg, whiteSpace: "nowrap",
    }}>{children}</span>
  );
}

export function Spark({ values = [], tip }) {
  const t = useDashTheme();
  const max = Math.max(...values, 1);
  const body = (
    <div style={{ display: "flex", alignItems: "flex-end", gap: 2, height: 20, marginTop: 10 }}>
      {values.map((v, i) => (
        <i key={i} className="tgcl-grow" style={{
          flex: 1, minHeight: 2, borderRadius: 1.5,
          height: `${Math.max(6, (v / max) * 100)}%`,
          background: t.c.primary, opacity: i === values.length - 1 ? 0.85 : 0.3,
          animationDelay: `${0.2 + i * 0.03}s`,
        }} />
      ))}
    </div>
  );
  return tip ? <Tooltip title={tip}>{body}</Tooltip> : body;
}

export function KSplit({ activePct = 0 }) {
  const t = useDashTheme();
  const a = Math.max(0, Math.min(100, Math.round(activePct)));
  return (
    <Tooltip title={`Active ${a}% · Idle ${100 - a}%`}>
      <div style={{ display: "flex", height: 9, borderRadius: 999, overflow: "hidden", marginTop: 12 }}>
        <span style={{ width: `${a}%`, background: t.status.positive }} />
        <span style={{ width: `${100 - a}%`, background: t.status.warning, opacity: 0.85 }} />
      </div>
    </Tooltip>
  );
}

export function Dots({ on = 0, total = 0 }) {
  const t = useDashTheme();
  const n = Math.max(0, Math.min(60, total));
  return (
    <Tooltip title={`${on} tracked time · ${Math.max(0, n - on)} silent`}>
      <div style={{ display: "flex", flexWrap: "wrap", gap: 3.5, marginTop: 12 }}>
        {Array.from({ length: n }, (_, j) => (
          <i key={j} style={{
            width: 9, height: 9, borderRadius: 999,
            background: j < on ? t.c.primary : "transparent",
            opacity: j < on ? 0.85 : 1,
            boxShadow: j < on ? "none" : `inset 0 0 0 1.6px ${t.status.negative}`,
          }} />
        ))}
      </div>
    </Tooltip>
  );
}

export function Bullet({ value = 0, target = 80 }) {
  const t = useDashTheme();
  const v = Math.max(0, Math.min(100, value));
  return (
    <Tooltip title={`This period ${v}% · target ${target}%`}>
      <div style={{ position: "relative", height: 14, borderRadius: 4, background: t.c.surfaceMuted, marginTop: 12 }}>
        <span style={{ position: "absolute", top: 0, bottom: 0, left: `${target}%`, right: 0, borderRadius: "0 4px 4px 0", background: t.status.positiveSoft }} />
        <span className="tgcl-widen" style={{ position: "absolute", left: 0, top: 4, bottom: 4, width: `${v}%`, background: t.c.primary, borderRadius: 2 }} />
        <span style={{ position: "absolute", top: -3, bottom: -3, left: `${target}%`, width: 2, background: t.c.text, borderRadius: 1 }} />
      </div>
    </Tooltip>
  );
}

export function GoalCells({ on = 0, total = 0 }) {
  const t = useDashTheme();
  const n = Math.max(1, Math.min(30, total));
  return (
    <Tooltip title={`${on} of ${n} goals done`}>
      <div style={{ display: "flex", gap: 3, marginTop: 12 }}>
        {Array.from({ length: n }, (_, j) => (
          <i key={j} className="tgcl-grow" style={{
            flex: 1, height: 9, borderRadius: 2,
            background: j < on ? t.status.positive : t.c.surfaceMuted,
            animationDelay: `${0.25 + j * 0.04}s`,
          }} />
        ))}
      </div>
    </Tooltip>
  );
}

/** Track bar used in ranked rows (tools) — share of the largest row. */
export function TrackBar({ pct = 0, color }) {
  const t = useDashTheme();
  return (
    <span style={{ width: 96, height: 6, borderRadius: 999, background: t.c.surfaceMuted, overflow: "hidden", flexShrink: 0, display: "block" }}>
      <span className="tgcl-widen" style={{ display: "block", height: "100%", borderRadius: 999, width: `${Math.max(3, pct)}%`, background: color || t.c.primary }} />
    </span>
  );
}

/* ══ KPI INSTRUMENTS ═══════════════════════════════════════════════════════
   Six tiles, six DIFFERENT instruments. Six identical sparks would be
   decoration; each shape below is chosen for the metric it carries.

   EVERY ONE READS A FIELD THE OVERVIEW ACTUALLY RETURNS. Where the mock showed
   something we do not store, the instrument was changed rather than faked —
   see IdleByDay and GaugeArc below.
   ═══════════════════════════════════════════════════════════════════════════ */

/**
 * AreaSpark — a trend with something to be a trend AGAINST.
 * `values` is this period, `prev` the one before it (daySeries.prevTotalMinutes),
 * drawn as a dashed ghost. Without the ghost a rising line means nothing.
 */
export function AreaSpark({ values = [], prev = [], color, height = 44, tip }) {
  const t = useDashTheme();
  const stroke = color || t.c.primary;
  const gid = React.useId();
  const all = values.concat(prev.filter((v) => Number.isFinite(v)));
  const max = Math.max(...all, 1);
  const W = 200;
  const pts = (arr) =>
    arr.map((v, i) => [
      (i / Math.max(1, arr.length - 1)) * W,
      height - ((Number(v) || 0) / max) * (height - 8) - 4,
    ]);
  const path = (arr) =>
    pts(arr).map((q, i) => `${i ? "L" : "M"}${q[0].toFixed(1)} ${q[1].toFixed(1)}`).join(" ");
  if (!values.length) return null;
  const d = path(values);
  const last = pts(values)[values.length - 1];
  const ghost = prev.filter((v) => Number.isFinite(v)).length >= 2 ? path(prev) : null;
  const body = (
    <svg viewBox={`0 0 ${W} ${height}`} preserveAspectRatio="none"
      style={{ display: "block", width: "100%", height, marginTop: 10 }}>
      <defs>
        <linearGradient id={gid} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={stroke} stopOpacity="0.30" />
          <stop offset="100%" stopColor={stroke} stopOpacity="0" />
        </linearGradient>
      </defs>
      <path d={`${d} L${W} ${height} L0 ${height} Z`} fill={`url(#${gid})`} />
      {ghost && (
        <path d={ghost} fill="none" stroke={t.c.borderStrong} strokeWidth="1.5"
          strokeDasharray="3 3" vectorEffect="non-scaling-stroke" />
      )}
      <path d={d} fill="none" stroke={stroke} strokeWidth="2" vectorEffect="non-scaling-stroke"
        strokeLinejoin="round" strokeLinecap="round" />
      <circle cx={last[0].toFixed(1)} cy={last[1].toFixed(1)} r="2.6" fill={stroke} />
    </svg>
  );
  return tip ? <Tooltip title={tip}>{body}</Tooltip> : body;
}

/** SplitBar — one length, two meanings, both figures stated beneath it. */
export function SplitBar({ activeMinutes = 0, idleMinutes = 0 }) {
  const t = useDashTheme();
  const tot = activeMinutes + idleMinutes;
  if (tot <= 0) {
    return (
      <span style={{ display: "block", height: 10, borderRadius: 999, marginTop: 12,
        background: t.c.surfaceMuted, boxShadow: `inset 0 0 0 1px ${t.c.border}` }} />
    );
  }
  const a = Math.round((activeMinutes / tot) * 100);
  return (
    <div style={{ marginTop: 12 }}>
      <div style={{ display: "flex", height: 10, borderRadius: 999, overflow: "hidden", gap: 2 }}>
        <span style={{ width: `${a}%`, background: t.status.positive, borderRadius: 999 }} />
        <span style={{ width: `${100 - a}%`, background: t.status.warning, borderRadius: 999 }} />
      </div>
      <div style={{ display: "flex", justifyContent: "space-between", marginTop: 7, fontSize: 10.5 }}>
        <span style={{ color: t.c.textSecondary, fontWeight: 600 }}>{a}% active</span>
        <span style={{ color: t.c.textSecondary, fontWeight: 600 }}>{100 - a}% idle</span>
      </div>
    </div>
  );
}

/**
 * IdleByDay — where idle actually lands.
 *
 * The mock drew idle by HOUR. We do not store that: weekMatrix carries ACTIVE
 * minutes per hour, not idle, and there is no idle-per-hour rollup anywhere.
 * Rather than invent a curve, this shows idle per DAY from
 * daySeries[].idleMinutes — real, and still answers "when does idle happen"
 * at the granularity we genuinely have. The worst day is marked.
 */
export function IdleByDay({ series = [] }) {
  const t = useDashTheme();
  const vals = series.map((d) => Number(d.idleMinutes) || 0);
  if (!vals.length) return null;
  const max = Math.max(...vals, 1);
  const worst = vals.indexOf(max);
  return (
    <div style={{ display: "flex", alignItems: "flex-end", gap: 3, height: 40, marginTop: 12 }}>
      {/* NOTE: native title, not antd Tooltip. One Tooltip per bar means one
          ResizeObserver per bar wrapped around a percentage-height child —
          the exact shape that fires "ResizeObserver loop completed with
          undelivered notifications". Seven bars is seven observers for a
          hover string. Do not convert these back. */}
      {vals.map((v, i) => (
        <i key={series[i]?.dateLocal || i} className="tgcl-grow"
          title={`${series[i]?.dateLocal || ""} · ${fmtMins(v)} idle`}
          style={{
            flex: 1, minHeight: 3, borderRadius: "3px 3px 0 0",
            height: `${Math.max(8, (v / max) * 100)}%`,
            background: t.status.warning,
            opacity: i === worst ? 1 : 0.42,
            animationDelay: `${0.2 + i * 0.03}s`,
          }} />
      ))}
    </div>
  );
}

/** Waffle — one cell per person. Three hollow cells are countable; "19/22" is not. */
export function Waffle({ on = 0, total = 0, max = 44 }) {
  const t = useDashTheme();
  const n = Math.max(0, Math.min(max, total));
  if (!n) return null;
  const cols = n <= 12 ? n : Math.ceil(n / 2) > 14 ? 14 : Math.ceil(n / 2);
  return (
    <Tooltip title={`${on} reporting · ${Math.max(0, n - on)} silent`}>
      <div style={{ display: "grid", gridTemplateColumns: `repeat(${cols}, 1fr)`, gap: 3.5, marginTop: 12 }}>
        {Array.from({ length: n }, (_, i) => (
          <i key={i} style={{
            aspectRatio: "1", borderRadius: 3,
            background: i < on ? t.status.positive : "transparent",
            boxShadow: i < on ? "none" : `inset 0 0 0 1.5px ${t.c.borderStrong}`,
          }} />
        ))}
      </div>
    </Tooltip>
  );
}

/** RankStubs — the shape of the tail, and it names the leader. */
export function RankStubs({ items = [], color }) {
  const t = useDashTheme();
  const rows = items.slice(0, 5);
  if (!rows.length) return null;
  const max = Math.max(...rows.map((r) => Number(r.value) || 0), 1);
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 4, marginTop: 12 }}>
      {/* native title here too — see the note in IdleByDay */}
      {rows.map((r, i) => (
        <div key={r.label + i} title={`${r.label} · ${fmtMins(r.value)}`}>
          <div style={{ display: "flex", alignItems: "center", gap: 7 }}>
            <span className="tgcl-widen" style={{
              height: 6, borderRadius: 999, flexShrink: 0,
              width: `${Math.max(6, ((Number(r.value) || 0) / max) * 62)}%`,
              background: color || t.c.primary, opacity: 1 - i * 0.15,
              animationDelay: `${0.2 + i * 0.05}s`,
            }} />
            <span style={{
              fontSize: 9.5, color: t.c.textTertiary, whiteSpace: "nowrap",
              overflow: "hidden", textOverflow: "ellipsis",
            }}>{r.label}</span>
          </div>
        </div>
      ))}
    </div>
  );
}

/**
 * GaugeArc — a half gauge, segmented by what we can actually count.
 *
 * The mock segmented goals into on-track / at-risk / behind. The goals payload
 * gives completedGoalsCount and totalGoalsCount and nothing else, so this
 * shows DONE against REMAINING. Splitting "remaining" into risk bands would be
 * a judgement we do not hold.
 */
export function GaugeArc({ done = 0, total = 0, height = 54 }) {
  const t = useDashTheme();
  if (!total) return null;
  const W = 150, sw = 9, cx = W / 2, cy = height - 4, r = height - 16;
  const pt = (ang) => [(cx + r * Math.cos(ang)).toFixed(1), (cy + r * Math.sin(ang)).toFixed(1)];
  const frac = Math.max(0, Math.min(1, done / total));
  const end = Math.PI + frac * Math.PI;
  return (
    <Tooltip title={`${done} of ${total} goals complete`}>
      <div style={{ marginTop: 10 }}>
        <svg viewBox={`0 0 ${W} ${height}`} preserveAspectRatio="xMidYMax meet"
          style={{ display: "block", width: "100%", height }}>
          <path d={`M${pt(Math.PI).join(" ")} A${r} ${r} 0 0 1 ${pt(2 * Math.PI).join(" ")}`}
            fill="none" stroke={t.c.surfaceMuted} strokeWidth={sw} strokeLinecap="round" />
          {frac > 0.01 && (
            <path d={`M${pt(Math.PI).join(" ")} A${r} ${r} 0 ${frac > 0.5 ? 1 : 0} 1 ${pt(end).join(" ")}`}
              fill="none" stroke={t.status.positive} strokeWidth={sw} strokeLinecap="round" />
          )}
        </svg>
        <div style={{ display: "flex", justifyContent: "center", gap: 13, marginTop: 2, fontSize: 10.5, color: t.c.textTertiary }}>
          <span><b style={{ color: t.c.textSecondary }}>{done}</b> done</span>
          <span><b style={{ color: t.c.textSecondary }}>{Math.max(0, total - done)}</b> open</span>
        </div>
      </div>
    </Tooltip>
  );
}

export const fmtMins = (m) => {
  const v = Math.max(0, Math.round(Number(m) || 0));
  const h = Math.floor(v / 60);
  return h ? `${h}h ${String(v % 60).padStart(2, "0")}m` : `${v}m`;
};
/**
 * Hours + minutes, never rounded — "12h 10m".
 *
 * This used to be `Math.round(m / 60) + "h"`, which dropped the minutes AND
 * could overstate: 12h31m rendered as "13h". It also read oddly next to the
 * precise values beside it ("3h 45m of 12h"). Kept under the `fmtH` name
 * because ~40 call sites use it; it is now just the precise formatter.
 */
export const fmtH = (m) => fmtMins(m);
